import React, { useEffect, useMemo, useRef, useState } from "react";
import { range } from "lodash-es";

import { renderFirstTruthyNode } from "@kikoff/client-utils/src/react";
import { useHover } from "@kikoff/components/src/v1/info/Tooltip";
import useId from "@kikoff/hooks/src/useId";
import useInitial from "@kikoff/hooks/src/useInitial";
import useSpringState from "@kikoff/hooks/src/useSpringState";
import { getProgress } from "@kikoff/utils/src/number";
import { combineClasses } from "@kikoff/utils/src/string";

import {
  maxScore,
  minScore,
  scoreTypeData,
  totalScoreRange,
} from "./constants";

import styles from "./credit_score_meter.module.scss";

interface CreditScoreMeterProps {
  className?: string;
  score: number;
  secondaryScore?: number;
  content?: React.ReactNode;
  contentAt?: {
    aboveScore?: React.ReactNode;
    belowScore?: React.ReactNode;
  };
  scoreType: keyof typeof scoreTypeData;
  springOptions?: useSpringState.Options;
  hideArrow?: boolean;
  strokeWidth?: number;
}

export default function CreditScoreMeter({
  className,
  score: _score,
  secondaryScore,
  contentAt = {},
  scoreType,
  springOptions,
  hideArrow = false,
  strokeWidth = 7,
}: CreditScoreMeterProps) {
  const score = _score || minScore;
  const unavailable =
    !_score ||
    // Equifax control codes should be handled as unavailable
    _score < minScore;
  const brackets = useMemo(
    () => Object.values(scoreTypeData[scoreType].brackets).sort(),
    [scoreType]
  );

  const initial = useInitial();
  const [hovering, hoverProps] = useHover();
  const [containerWidth, setContainerWidth] = useState(0);

  const maskId = useId();
  const width = 250;
  const markerSize = 10;
  const height =
    width / 2 +
    // Prevent end linecap cutoff
    strokeWidth;
  const bracketGap = /* 10 */ 0;

  const svgTextHeightMultipler = 1.79;
  const labelHeight = /* 18 */ 0 / Math.max(containerWidth / 400, 1);
  const labelSize = labelHeight / svgTextHeightMultipler;
  const labelDistance = labelHeight * 0.1;

  const topPadding = strokeWidth / 2 + labelHeight + labelDistance;
  const radius = width / 2 - topPadding;
  const commonCircleProps = {
    strokeWidth,
    r: radius,
    cx: width / 2,
    cy: width / 2 + strokeWidth * 2,
  };

  const semicircumference = radius * Math.PI;
  const spaceWithoutGaps =
    semicircumference - bracketGap * (brackets.length - 1);

  const currentScore = useSpringState(score, {
    valueBounds: [minScore, maxScore],
    ...springOptions,
  });

  const meterRef = useRef<SVGCircleElement>(null);

  const edgePosition = `translate(${commonCircleProps.cx} ${
    commonCircleProps.cy - commonCircleProps.r
  })`;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { current } = containerRef;

    setContainerWidth(current.clientWidth);

    const observer = new ResizeObserver(() => {
      setContainerWidth(current.clientWidth);
    });

    observer.observe(current);
    return () => {
      observer.disconnect();
    };
  }, []);
  return (
    <div
      className={combineClasses(styles["credit-score-meter"], className)}
      ref={containerRef}
      {...hoverProps}
    >
      <svg viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <mask id={maskId}>
            <circle
              stroke="white"
              {...commonCircleProps}
              strokeDasharray={`${(() => {
                return brackets
                  .map((bracket, i) => {
                    const rangeMin = bracket;
                    const rangeMax = brackets[i + 1] || maxScore;
                    const spacePercentage =
                      (rangeMax - rangeMin) / totalScoreRange;

                    return spacePercentage * spaceWithoutGaps;
                  })
                  .join(` ${bracketGap} `);
              })()} ${semicircumference}`}
            />
          </mask>
        </defs>
        <g className={styles.transform}>
          {/* Bracket labels */}
          <g transform={edgePosition}>
            {(() => {
              const getTextElement = (
                children: React.ReactNode,
                rotation,
                props: JSX.IntrinsicElements["text"],
                offsetMultiplier: number
              ) => (
                <text
                  alignmentBaseline="central"
                  style={{
                    transition: "transform 0.2s, opacity 0.15s",
                    transformOrigin: `0 ${commonCircleProps.r}px`,
                    transform: [
                      `rotate(${90 + rotation}deg)`,
                      `translate(${(strokeWidth / 4) * offsetMultiplier}px, -${
                        hovering ? labelSize + labelDistance : 0
                      }px)`,
                    ].join(" "),
                    opacity: hovering ? 1 : 0,
                  }}
                  fontSize={labelSize}
                  {...props}
                >
                  {children}
                </text>
              );

              return [
                ...(function* () {
                  let arcBefore = 0;
                  for (const i of range(brackets.length)) {
                    if (i > 0) {
                      const spacePercentage =
                        (brackets[i] - brackets[i - 1]) / totalScoreRange;
                      arcBefore +=
                        spacePercentage * spaceWithoutGaps + bracketGap;
                    }

                    yield getTextElement(
                      brackets[i],
                      (arcBefore / semicircumference) * 180,
                      { key: i },
                      -1
                    );
                  }
                })(),
                getTextElement(
                  maxScore,
                  180,
                  {
                    key: "max",
                    textAnchor: "end",
                  },
                  1
                ),
              ];
            })()}
          </g>
          {(() => {
            const animationStartScore = useMemo(() => currentScore, [score]);
            const animationProgress = getProgress(
              [animationStartScore, score],
              currentScore
            );

            // Total gap amount the filled portion covers
            const getFilledGapTotal = (s) =>
              brackets
                .slice(1)
                .reduce((acc, curr) => acc + (s >= curr ? bracketGap : 0), 0);

            const getTargetFill = (s: number) =>
              getProgress([minScore, maxScore], s) * spaceWithoutGaps +
              getFilledGapTotal(s);
            const targetFillAmount = useMemo(() => getTargetFill(score), [
              score,
              containerWidth,
            ]);

            const fillAmountRef = useRef(0);
            const prevFillAmount = useMemo(() => fillAmountRef.current, [
              score,
            ]);
            fillAmountRef.current =
              (targetFillAmount - prevFillAmount) * animationProgress +
              prevFillAmount;

            const secondaryFill = getTargetFill(secondaryScore);

            const showSecondary =
              secondaryScore && secondaryScore !== currentScore;
            const values = [
              showSecondary && {
                fill: secondaryFill,
                color: "var(--primary-weak)",
              },
              {
                fill: fillAmountRef.current,
                color: "var(--primary)",
              },
            ];

            return (
              <>
                {/* Segmented circle */}
                <g mask={`url(#${maskId})`}>
                  <circle
                    {...commonCircleProps}
                    stroke="var(--outline)"
                    strokeDasharray={semicircumference}
                  />
                  {values.map(
                    (value, i) =>
                      value && (
                        <circle
                          key={i}
                          {...commonCircleProps}
                          ref={meterRef}
                          strokeDasharray={`${value.fill} ${
                            semicircumference + value.fill + strokeWidth
                          }`}
                          stroke={value.color}
                        />
                      )
                  )}
                </g>
                {!hideArrow &&
                  values.map(
                    (value, i) =>
                      value && (
                        <path
                          key={i}
                          transform-origin={`0 ${commonCircleProps.r}`}
                          transform={[
                            edgePosition,
                            `rotate(${
                              90 + (value.fill / semicircumference) * 180
                            })`,
                            `translate(${-markerSize / 2} ${strokeWidth})`,
                          ].join(" ")}
                          d="M4.24509 0.689086C4.62999 0.0224191 5.59224 0.02242 5.97714 0.689087L9.3734 6.57158C9.7583 7.23824 9.27717 8.07158 8.50737 8.07158H1.71485C0.945054 8.07158 0.46393 7.23824 0.84883 6.57158L4.24509 0.689086Z"
                          fill={value.color}
                        />
                      )
                  )}
              </>
            );
          })()}
        </g>
      </svg>
      {/* Inner text content, first render may not have correct sizing */}
      {!initial &&
        (() => {
          // Reference width to scale content from. If element width differs,
          // content will be scaled proportionally to mimic svg behavior
          const referenceWidth = 300;
          const squareSideSvgUnits =
            (commonCircleProps.r - strokeWidth / 2) * Math.SQRT2;

          // Prevent content from scaling too quickly, text will look awkward otherwise
          const scale = (containerWidth / referenceWidth + 1) / 2;
          const scaledWidth = containerWidth / scale;
          const squareSidePx = (squareSideSvgUnits / width) * scaledWidth;

          const position: React.CSSProperties = {
            transform: `scale(${scale})`,
            bottom: `${(strokeWidth / width) * scaledWidth}px`,
            left: `${(scaledWidth - squareSidePx) / 2}px`,
            width: `${squareSidePx}px`,
            height: `${squareSidePx / 2}px`,
          };

          return (
            <div
              className={styles.content}
              style={{
                transformOrigin: [
                  `${-Number.parseFloat(position.left as string)}px`,
                  // Set origin from bottom since block is positioned from there
                  `${
                    Number.parseFloat(position.bottom as string) +
                    // Height
                    squareSidePx / 2
                  }px`,
                ].join(" "),
                ...position,
              }}
            >
              {contentAt.aboveScore || <div />}
              <div className={`${styles.score} text:heading-2`}>
                {unavailable ? "N/A" : Math.round(currentScore)}
              </div>
              {contentAt.belowScore}
            </div>
          );
        })()}
    </div>
  );
}

interface MiniCreditScoreMeterProps {
  className?: string;
  score: number;
  blur?: boolean;
}

export function MiniCreditScoreMeter({
  className,
  score,
  blur,
}: MiniCreditScoreMeterProps) {
  const r = 45;
  const semicircumference = r * Math.PI;
  const progress = getProgress([minScore, maxScore], score);
  const unavailable = score < minScore;

  return (
    <svg
      className={combineClasses(
        styles["mini-credit-score-meter"],
        blur && styles.blur,
        className
      )}
      viewBox="0 0 100 50"
      style={
        {
          "--progress": progress * semicircumference,
          "--radius": r,
        } as React.CSSProperties
      }
    >
      <circle cx="50%" cy="100%" r={r} />
      <circle cx="50%" cy="100%" r={r} />
      <text x="50%" y="45">
        {renderFirstTruthyNode(
          () => blur && "--",
          () => unavailable && "N/A",
          () => score
        )}
      </text>
    </svg>
  );
}
